import { ReactElement, memo, useEffect, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

import { ICatalogueCustomization } from "../../../../apis/useGetCatalogueData/types";
import { Nullable } from "../../../../common/types/types";
import Condition from "../../../../components/Conditional/Condition";
import I18nText from "../../../../components/I18nText/I18nText";
import AccordionSelector from "../../../../components/elements/Selection/accordion/AccordionSelector";
import { PDP_INPUT_STATUS } from "../../constants";
import { ICustomizationSetItem } from "../setitems/types";
import { useCustomizationChangeHandler } from "./useCustomizationChangeHandler";
import { useCustomizationOptionsSelections } from "./useCustomizationOptionsSelections";

interface IProps {
	idx: number;
	customization: ICatalogueCustomization;
	setitem?: Nullable<ICustomizationSetItem>;
	selectedCustomization?: ICatalogueCustomization | null;
}

const PDPCustomization = ({
	idx,
	customization,
	setitem,
	selectedCustomization,
}: IProps): ReactElement => {
	const { selections } = useCustomizationOptionsSelections(
		customization.food_supplier_customization_options
	);

	const { onChange, withinQty, selection } = useCustomizationChangeHandler(
		idx,
		customization,
		setitem,
		selectedCustomization
	);

	const { t } = useTranslation();

	const memoizedOnChange = useMemo(() => onChange(selections), [onChange, selections]);

	// Auto-trigger selection for single option required customizations
	useEffect(() => {
		if (
			customization.min_permitted >= 1 &&
			customization.max_permitted === 1 &&
			selections.length === 1 &&
			selection.length === 0
		) {
			// Only auto-select if there's exactly one option and it's required
			const changeHandler = onChange(selections);

			changeHandler([selections[0].id]);
		}
	}, [
		customization.min_permitted,
		customization.max_permitted,
		selections.length,
		selection.length,
		onChange,
	]);

	const config = useMemo(() => {
		const msg = withinQty ? "" : t("Error.required_customization");
		const error = (msg ?? "").split("{count}").join(String(customization.min_permitted ?? ""));

		// Set initValues from selectedCustomization
		const initValues =
			selectedCustomization?.food_supplier_customization_options?.map((option) =>
				String(option.id ?? "")
			) || [];

		return {
			id: `Customization-${customization.id}`,
			options: selections,
			label: <Trans i18nKey="Generic.Select" />,
			testId: `Customization-${customization.title}`,
			onChange: memoizedOnChange,
			autoCollapse: false,
			selectMultiple: customization.max_permitted > 1,
			min: customization.min_permitted,
			max: customization.max_permitted,
			allowMultiplePerItem: false,
			initValues,
			erroren: error,
			errorcn: error,
		};
	}, [customization, selections, memoizedOnChange, withinQty, selectedCustomization, t]);

	return (
		<div
			className="PDPCustomizationContainer"
			data-inputstatus={withinQty ? PDP_INPUT_STATUS.OK : PDP_INPUT_STATUS.BAD}
		>
			<h2>
				<span className="OptionTitle">
					<I18nText enText={customization.title} cnText={customization.title_cn}>
						<Condition check={customization.min_permitted >= 1}>
							<span className="Required">*</span>
							<Condition check={customization.max_permitted > 1}>
								<span className="MinMax">{`(${selection.length}/${customization.max_permitted})`}</span>
							</Condition>
						</Condition>
					</I18nText>
				</span>
				<Condition check={customization.min_permitted === 0}>
					<span className="Optional">
						<Trans i18nKey="Generic.optional" />
						<Condition check={customization.max_permitted > 1}>
							<span className="MinMaxOptional">{`(${selection.length}/${customization.max_permitted})`}</span>
						</Condition>
					</span>
				</Condition>
			</h2>
			<AccordionSelector
				key={selectedCustomization?.id || `default-${customization.id}`}
				config={config}
			/>
		</div>
	);
};

export default memo(PDPCustomization);
